import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { reviewPlatformKeys } from '@/lib/queryKeys';
import type { SelectBusinessDto, SelectBusinessResponse } from '@/types/review-platform';

interface SelectBusinessVariables extends SelectBusinessDto {
  platformSlug: 'google' | 'facebook';
}

/**
 * Final step of the OAuth flow — the contractor picks a Google location or a
 * Facebook page from the list returned by `useAvailableBusinesses`. The
 * backend persists the choice on `PlatformConfig.externalBusinessId` and flips
 * status to CONNECTED.
 *
 * POST /v2/review-platforms/oauth/:platform/select-business
 */
export function useSelectBusinessLocation(locationId: string | null = null) {
  const queryClient = useQueryClient();
  return useApiMutation<SelectBusinessResponse, SelectBusinessVariables>({
    mutationFn: ({ platformSlug, ...body }) =>
      apiPost<SelectBusinessResponse, SelectBusinessDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.oauthSelectBusiness(platformSlug),
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      toast.success('Platform connected');
    },
  });
}
